import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import AddressForm from "./AddressForm";
import { MapPin, House, Briefcase, Plus, PencilSimple, Trash, CheckCircle, X } from "@phosphor-icons/react";

/**
 * Address selector modal — lists saved addresses, lets user pick one, add/edit/delete.
 */
export default function AddressSelector({ onClose, onSelect, selectedId, title = "Choose delivery address" }) {
  const { selectedAddress, setSelectedAddress } = useCart();
  const [addrs, setAddrs] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/customer/addresses");
      setAddrs(r.data);
      // Sync cart's selected with server state
      if (selectedAddress) {
        const updated = r.data.find((a) => a.id === selectedAddress.id);
        if (!updated) {
          const def = r.data.find((a) => a.is_default) || r.data[0] || null;
          setSelectedAddress(def);
        } else if (JSON.stringify(updated) !== JSON.stringify(selectedAddress)) {
          setSelectedAddress(updated);
        }
      }
    } catch {
      setAddrs([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (a) => {
    if (!confirm(`Delete "${a.label}" address?`)) return;
    try {
      await api.delete(`/customer/addresses/${a.id}`);
      toast.success("Address removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const setDefault = async (a) => {
    try {
      await api.post(`/customer/addresses/${a.id}/default`);
      toast.success("Default address updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 fade-up"
        onClick={onClose}>
        <div className="bg-white w-full max-w-md max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl shadow-2xl p-5"
          onClick={(e) => e.stopPropagation()}
          data-testid="address-selector-modal">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-black text-xl">{title}</h2>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center" aria-label="Close">
              <X size={16} />
            </button>
          </div>

          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="address-add-new"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-[#15803D] text-[#15803D] hover:bg-[#15803D]/5 transition font-bold text-sm mb-4">
            <Plus size={16} weight="bold" />
            Add new address
          </button>

          {addrs === null && <div className="py-8 text-center text-sm text-gray-400">Loading…</div>}

          {addrs?.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-500">
              No saved addresses yet. Tap above to add your first one.
            </div>
          )}

          <div className="space-y-3">
            {addrs?.map((a) => {
              const Icon = a.label === "Work" ? Briefcase : a.label === "Other" ? MapPin : House;
              const active = a.id === selectedId;
              return (
                <div key={a.id}
                  className={`qd-card p-3.5 ${active ? "border-2 border-[#15803D]" : "border border-gray-100"}`}
                  data-testid={`address-item-${a.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[#F8CB46]/30 flex items-center justify-center flex-shrink-0">
                      <Icon size={16} weight="fill" className="text-[#15803D]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-heading font-black text-sm">{a.label}</span>
                        {a.is_default && (
                          <span className="text-[9px] uppercase tracking-widest font-bold bg-[#15803D] text-white px-1.5 py-0.5 rounded-full">Default</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-700">{a.receiver_name} · {a.receiver_mobile}</div>
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                        {a.address}{a.landmark ? `, ${a.landmark}` : ""}, {a.city ? `${a.city}, ` : ""}<span className="font-mono">{a.pincode}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button onClick={() => onSelect(a)}
                      className="flex-1 bg-[#15803D] text-white text-xs font-bold py-1.5 rounded-lg hover:bg-[#166534]"
                      data-testid={`address-select-${a.id}`}>
                      {active ? "Selected" : "Deliver here"}
                    </button>
                    {!a.is_default && (
                      <button onClick={() => setDefault(a)}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center gap-1"
                        data-testid={`address-default-${a.id}`}>
                        <CheckCircle size={14} /> Default
                      </button>
                    )}
                    <button onClick={() => { setEditing(a); setShowForm(true); }}
                      className="text-xs font-bold p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200"
                      data-testid={`address-edit-${a.id}`}>
                      <PencilSimple size={14} />
                    </button>
                    <button onClick={() => remove(a)}
                      className="text-xs font-bold p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                      data-testid={`address-delete-${a.id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showForm && (
        <AddressForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={(saved) => {
            setShowForm(false);
            setEditing(null);
            load();
            // If this was a fresh add and there was no prior selection, auto-select
            if (!editing && !selectedId && saved) onSelect(saved);
          }}
        />
      )}
    </>
  );
}
